import React, {useState, useCallback, useMemo, useRef} from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, hasAtLeastOnePermission, formatDate, getErasureReportStatus, workDiagnoseReportType } from '../../../sc-utilities';
import { CloudDownload, Replay } from '@material-ui/icons';
import * as actions from './store/reportActions';
import ReportList from './ReportList';
import ReportRegenerate from './ReportRegenerate';
import { informationColors } from '../../../../themes';
import {download} from "../../../sc-utilities/download.utils";
import config from "../../../../config/config";
import {snackActions} from "../../../sc-utilities/SnackBar";

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
});
let downloadObj = {}

const Report = props => {
    const tableRef = useRef();
    const { classes, columns, onGenerate, translations, permissions, activeCustomerID, productID, onRegenerateReport, isSingleTab, erasureReportType = 'pdf' } = props;
    let visible = columns && columns['ReportProduct'];
    const [regenerateAction, setRegenerateAction] = useState(0);
    const [regenerateModalState, setRegenerateModalState] = useState({ open: false, erasureReportId: null });

    const erasureReportTypeRegenerateTitleMapping = useMemo(() => {
        return {
            'pdf': translations["regenerate_erasure_report"] || "Regenerate erasure report",
            'verification-pdf': translations["regenerate_erasure_verification_report"] || "Regenerate erasure verification report",
            'check-pdf': translations["regenerate_asset_check_report"] || 'Regenerate asset check report'
        };
    }, [translations]);

    const handleCloseSafe = () => {
        setRegenerateModalState({ open: false, erasureReportId: null });
    };

    const handleCloseDanger = useCallback(async (erasureReportId = null) => {
        let result = await onRegenerateReport({ ReportID: erasureReportId ? erasureReportId : regenerateModalState.erasureReportId, ActiveCustomerID: activeCustomerID });
        setRegenerateModalState({ open: false, erasureReportId: null });

        if (result.data) {
            setRegenerateAction(regenerateAction + 1);
        }
        tableRef.current && tableRef.current.onQueryChange();
    }, [regenerateModalState, regenerateAction, onRegenerateReport, activeCustomerID]);

    const handleRegenerate = useCallback((erasureReportId, generatedStatus) => {
        if (generatedStatus === -1) {
            handleCloseDanger(erasureReportId);
        } else {
            setRegenerateModalState({ ...regenerateModalState, open: true, erasureReportId: erasureReportId });
        }
    }, [regenerateModalState, handleCloseDanger]);

    // const translateErasureReportType = useCallback((type) => {
    //     switch (type) {
    //         case "pdf":
    //             return translations['pdf'] || 'PDF';
    //         case "component":
    //             return translations['component'] || 'Component';
    //         default:
    //             return translations['short_not_assigned'] || 'n/a';
    //     }

    // }, [translations])


    let tableColumns = useMemo(() => {

        const downloadReport = async erasureReportID => {
            //await onDownload(erasureReportID, { ActiveCustomerID: activeCustomerID });
            const objectURL = `${config.backend_domain}:${config.backend_port}/report/download?ReportID=${erasureReportID}&ErasureReportType=${erasureReportType}&ActiveCustomerID=${activeCustomerID}`;
            let exportDataSuccessful = await download(objectURL);
            tableRef.current && tableRef.current.onQueryChange();
            if (!exportDataSuccessful) {
                return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
            }
        }

        const createAction = (erasureReportID, originalFileName, generatedStatus = null, generated, translations) => {
            return <div>
                {hasPermission(permissions, 'erasure_report_download') && generatedStatus === 2 ?
                    <Tooltip title={translations["download_report"] || "Download report(s)"}>
                        <IconButton component='div' disabled={downloadObj ? downloadObj[erasureReportID] : false} aria-label={translations["download_report"] || "Download report(s)"}
                            onClick={() => {
                                if (downloadObj ? downloadObj[erasureReportID] : false) return null; else return downloadReport(erasureReportID, originalFileName)
                            }}>
                            {<CloudDownload />}
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {hasPermission(permissions, 'erasure_report_regenerate') && (generatedStatus === 1 || generatedStatus === -1) ?
                    <Tooltip title={erasureReportTypeRegenerateTitleMapping[erasureReportType]}>
                        <IconButton onClick={() => handleRegenerate(erasureReportID, generatedStatus)} component='div' aria-label={erasureReportTypeRegenerateTitleMapping[erasureReportType]}>
                            <Replay />
                        </IconButton>
                    </Tooltip>
                    : null
                }
            </div>
        };
        return [
            {
                title: translations["actions"] || 'Action(s)', field: 'PdfReportID',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 200, maxWidth: 200 },
                filtering: false,
                render: rowData => {
                    return <>
                        {createAction(rowData.ErasureReportID, rowData.OriginalFileName, rowData.GeneratedStatus, rowData.Generated, translations)}
                    </>
                }
            },
            {
                title: translations["requested_by"] || 'Requested By', field: 'Username',
                hidden: visible ? visible['Username'] : false,
                cellStyle: { minWidth: 220, maxWidth: 220 },
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.User && rowData.User.Username ? rowData.User.Username : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["requested_at"] || 'Requested at', field: 'RequestedAt',
                hidden: visible ? visible['RequestedAt'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.RequestedAt ? formatDate(rowData.RequestedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["number_of_downloads"] || 'Number of downloads', field: 'DownloadedNumber',
                hidden: visible ? visible['DownloadedNumber'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.DownloadedNumber ? rowData.DownloadedNumber : '0'}</Typography>;
                }
            },
            {
                title: translations["original_file_name"] || 'Original file name', field: 'OriginalFileName',
                hidden: visible ? visible['OriginalFileName'] : false,
                filtering: true,
                selection: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.OriginalFileName ? rowData.OriginalFileName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            // {
            //     title: translations["erasure_report_type"] || 'Erasure report type', field: 'ErasureReportType',
            //     hidden: visible ? visible['ErasureReportType'] : false,
            //     cellStyle: { minWidth: 40, maxWidth: 40 },
            //     lookup: workDiagnoseReportType(translations)?.lookup,
            //     defaultFilter: workDiagnoseReportType(translations)?.defaultFilter,
            //     filtering: true,
            //     render: rowData => {
            //         return <Typography variant={'caption'}>{rowData.ErasureReportType ? translateErasureReportType(rowData.ErasureReportType) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            //     }
            // },
            {
                title: translations["status"] || 'Status', field: 'GeneratedStatus',
                hidden: visible ? visible['GeneratedStatus'] : false,
                lookup: { '0': translations['ready_to_generate'] || 'Ready to generate', '1': translations['in_progress'] || "In progress", '2': translations['generated'] || 'Generated', '-1': translations['failed'] || 'Failed' },
                defaultFilter: ['0', '1', '2', '-1'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <CustomChip trimming={true} customChipKey={rowData.ColliInventoryID}
                        className={`chip${getErasureReportStatus(rowData.GeneratedStatus, 'forStyle')}`}
                        value={getErasureReportStatus(rowData.GeneratedStatus, null, null, translations)} />
                }
            },
        ]
    }, [translations, visible, activeCustomerID, permissions, handleRegenerate, erasureReportType, erasureReportTypeRegenerateTitleMapping ]);

    if (!hasAtLeastOnePermission(permissions, [
        "erasure_report_download",
        "system_erasure_report_delete"
    ])) {
        tableColumns.shift();
    };

    return (
        hasPermission(permissions, 'erasure_report_list') ?
            <div className={classes.root}>
                <div >
                    <ReportList
                        onGenerate={onGenerate}
                        tableColumns={tableColumns}
                        productID={productID}
                        tableRef={tableRef}
                        isSingleTab={isSingleTab}
                        erasureReportType={erasureReportType}
                    />
                </div>
                {hasPermission(permissions, 'erasure_report_regenerate') ? (
                    <ReportRegenerate
                        entityName={translations['report'] || 'Report'}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        regenerateModalState={regenerateModalState}
                    />
                ) : null}
            </div> : null
    );
};

export const CustomChip = props => {
    const { keyValue, value, className } = props;
    let possibleStyles = {
        chipFailed: {
            color: informationColors.error.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipReady: {
            color: informationColors.warning.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipInProgress:
        {
            color: informationColors.info.dark,
            fontSize: 14,
            display: 'inline-block'

        },
        chipGenerated: {
            color: informationColors.success.dark,
            fontSize: 14,
            display: 'inline-block'

        }
    }
    return (
        <Typography key={keyValue} className={className} style={possibleStyles[className]}>{value === 'n/a' ? value : capitalize(value)}</Typography>
    );
};

const capitalize = (s) => {
    if (typeof s !== 'string') return ''
    return s.charAt(0).toUpperCase() + s.slice(1)
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state?.translate?.translations?.activeTranslations || {},
        columns: state?.core?.login?.userSettings?.Columns || {},
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onRegenerateReport: data => dispatch(actions.regenerateReport(data)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(Report));
